import './style.scss';

import { doc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/init';
import { getRequestShop } from '../../redux/slices/env';

import { useAppSelector } from '../../redux/hooks';
import { getSections } from '../../redux/slices/navigation';


import { Modal, TitleBar } from '@shopify/app-bridge-react';
import {
  Bleed,
  Box,
  Button,
  ButtonGroup,
  Card,
  Divider,
  Icon,
  InlineStack,
  Label,
  Layout,
  ResourceItem,
  Text,
  TextField
} from "@shopify/polaris";

import {
  ChevronUpIcon,
  ChevronDownIcon,
  PlusIcon,
} from '@shopify/polaris-icons';

import * as icons from '@shopify/polaris-icons';
import type { NavigationSection } from '@/shared/typings/types/guide';

type IconComponent = React.FunctionComponent<React.SVGProps<SVGSVGElement>>;

function resolveIcon(name?: string | null): IconComponent | (() => React.JSX.Element) | null {
  if (!name) return () => <svg></svg>;
  const iconsRecord = icons as Record<string, IconComponent>;
  if (iconsRecord[name]) return iconsRecord[name];
  let cleanName = name.replace(/(Major|Minor)$/, '');
  if (cleanName === 'CirclePlus') cleanName = 'PlusCircle';
  if (cleanName === 'MobileCancel') cleanName = 'X';
  const iconName = `${cleanName}Icon`;
  return iconsRecord[iconName] || iconsRecord[cleanName] || null;
}

import uniqid from 'uniqid';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { arrayMove, objectToArray } from 'utils/helpers';

export default function Settings() {
  const shop = useAppSelector(getRequestShop);
  const [editId, setEditId] = useState<string | null>(null);
  const sections = useAppSelector(getSections);

  const [lazySections, setLazySections] = useState<Record<string, NavigationSection>>(sections);

  useEffect(() => {
    setLazySections(sections);
  }, [sections]);

  const dbRef = `shops/${shop}/settings/navigation`

  const moveSection = useCallback((currentIndex: number, direction: 'up' | 'down') => {
    let arr = objectToArray(lazySections);
    let targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= arr.length) return;

    let newArr = arrayMove(arr, currentIndex, targetIndex);
    let updatedSections = newArr.reduce((acc, item, idx) => {
      acc[item.id] = { ...item, index: idx };
      return acc;
    }, {} as Record<string, NavigationSection>);

    setLazySections(updatedSections);
    setDoc(doc(db, dbRef), { sections: updatedSections }, { merge: true });
  }, [db, dbRef, lazySections]);

  const closeEditor = useCallback(() => setEditId(null), [])

  const editSection = useCallback((id: string) => setEditId(id), [])

  const handleSave = useCallback((passedSections?: Record<string, NavigationSection>) => {
    let sections = passedSections || lazySections
    setDoc(doc(db, dbRef), { sections }, { merge: true })
  }, [db, dbRef, lazySections])

  const handleChange = useCallback((section: Partial<NavigationSection>, save?: boolean) => {
    let _sections = { ...sections, [editId as string]: { id: editId as string, label: '', icon: null, ...section } as NavigationSection }
    setLazySections(_sections)
    if (save) handleSave(_sections)
  }, [sections, handleSave, editId])

  const handleAddSection = useCallback(() => setEditId(uniqid()), [])

  const handleDeleteSection = useCallback(() => {
    let _sections = { ...sections }
    delete _sections[editId as string]
    setLazySections(_sections)
    updateDoc(doc(db, dbRef), { sections: _sections })
    setEditId(null)

  }, [db, dbRef, sections, editId])

  const RenderItem = (item: {
    id: string;
    onClick?: () => void;
    label?: string;
    icon?: string;
    isFirst: boolean;
    isLast: boolean;
    onMoveUp: () => void;
    onMoveDown: () => void;
  }) => {
    const { id, onClick, label, icon, isFirst, isLast, onMoveUp, onMoveDown } = item;
    return (
      <div className="Polaris-ResourceList__ItemWrapper">
        <ResourceItem id={id} onClick={onClick || (() => { })}>
          <InlineStack align="space-between" blockAlign="center" gap="300">
            <InlineStack blockAlign="center" gap="300">
              <span onClick={(e) => e.stopPropagation()}>
                <ButtonGroup variant="segmented">
                  <Button
                    icon={ChevronUpIcon}
                    disabled={isFirst}
                    onClick={onMoveUp}
                    accessibilityLabel="Move up"
                  />
                  <Button
                    icon={ChevronDownIcon}
                    disabled={isLast}
                    onClick={onMoveDown}
                    accessibilityLabel="Move down"
                  />
                </ButtonGroup>
              </span>
              <Box>
                <Icon source={resolveIcon(icon)} tone="base" />
              </Box>
              <Text as="span" fontWeight="semibold">{label}</Text>
            </InlineStack>
            <Button onClick={onClick}>Edit</Button>
          </InlineStack>
        </ResourceItem>

      </div>
    );
  }

  const sectionsList = useMemo(() => objectToArray(lazySections), [lazySections]);

  return (
    <Layout.AnnotatedSection
      title="Sections"
      description="Sections group articles in your guide. Use up and down buttons to reorder, and edit to change title and icon."
    >
      <Card padding="0">
        <div className="Polaris-ResourceList">
          <div>
            {sectionsList.map((item, index) => (
              <RenderItem
                key={item.id}
                {...item}
                isFirst={index === 0}
                isLast={index === sectionsList.length - 1}
                onMoveUp={() => moveSection(index, 'up')}
                onMoveDown={() => moveSection(index, 'down')}
                onClick={() => editSection(item.id)}
              />
            ))}
          </div>
        </div>
        <Divider />
        <ResourceItem id="add" onClick={handleAddSection} media={<Icon source={PlusIcon} />}>

          <Text as="span" fontWeight="medium">Add Section</Text>
        </ResourceItem>
      </Card>

      <SectionEditor
        active={Boolean(editId)}
        onClose={closeEditor}
        section={lazySections[editId as string]}
        onSave={handleSave}
        onChange={handleChange}
        onDelete={handleDeleteSection}
        sectionsLength={Object.keys(sections || {}).length}
      />

    </Layout.AnnotatedSection>

  )
}

interface SectionEditorProps {
  active: boolean;
  onClose: () => void;
  onSave: (s?: Record<string, NavigationSection>) => void;
  onChange: (s: Partial<NavigationSection>, save?: boolean) => void;
  onDelete: () => void;
  section?: Partial<NavigationSection>;
  sectionsLength: number;
}

const SectionEditor = ({ active, onClose, onSave, onChange, onDelete, section = {}, sectionsLength }: SectionEditorProps) => {
  const excludeIcons: string[] = []
  const iconList = Object.keys(icons).filter(i => (!excludeIcons.includes(i) && i.indexOf('Twotone') === -1 && i.indexOf('Minor') === -1))

  const handleChange = useCallback(
    <K extends keyof NavigationSection>(val: NavigationSection[K] | null, key: K, save?: boolean) => {
      const s = { ...section, [key]: val };
      onChange(s, save);
    },
    [onChange, section]
  );

  useEffect(() => {
    if (active && section.index === undefined) {
      handleChange(sectionsLength, 'index')
    }
  }, [active, section, sectionsLength, handleChange])

  const canDelete = useMemo(() => sectionsLength > 1, [sectionsLength])

  const handleSelect = useCallback((icon: string) => {
    handleChange(icon === section.icon ? null : icon, 'icon', true)
  }, [handleChange, section.icon])


  return (
    <Modal id="section-editor-modal" open={active} onHide={onClose}>
      <TitleBar title={section.label || 'Edit section'}>
        <button tone="critical" onClick={onDelete} disabled={!canDelete}>Delete</button>
        <button variant="primary" onClick={onClose}>Done</button>
      </TitleBar>
      <Box padding="400">
        <TextField
          autoComplete="off"
          label="Section title"
          labelHidden
          placeholder="Section title"
          value={section.label}
          onChange={(v) => handleChange(v, 'label')}
          onBlur={() => onSave()}
          prefix={section.icon ? <Icon source={resolveIcon(section.icon)} tone="base" /> : undefined}
        />
        <Box paddingBlockStart={"600"}>
          <InlineStack align="space-between" blockAlign="center">
            <Label id="icon">Icon</Label>
            <Button variant="plain" onClick={() => handleChange(null, 'icon', true)}>Clear</Button>
          </InlineStack>
        </Box>
        <Box paddingBlockStart={"200"}>
          <InlineStack gap="200">
            {iconList.map(i => (
              <SectionIcon
                key={i}
                id={i}
                active={section.icon === i}
                onSelect={handleSelect}
              />
            ))}
          </InlineStack>
        </Box>

      </Box>
    </Modal>
  )
}

const SectionIcon = ({ id, active, onSelect }: { id: string; active: boolean; onSelect: (id: string) => void }) => {
  const handleSelect = useCallback(() => onSelect(id), [id, onSelect])
  const icon = (icons as Record<string, React.FunctionComponent<React.SVGProps<SVGSVGElement>>>)[id]
  return (
    <Bleed marginBlock={active ? "100" : "0"} marginInline={active ? "100" : "0"}>
      <Button variant={active ? "primary" : "tertiary"} icon={icon} onClick={handleSelect} />
    </Bleed>
  )
}